import { ApolloError, ServerError } from "@apollo/client";
import { GraphQLError, GraphQLFormattedError } from "graphql";
import { isArray, isString } from "lodash-es";

import { IntlContextValue, useIntl } from "~frontend/providers/IntlProvider";

export function getApolloErrorMessage(
  error: ApolloError,
  intl: IntlContextValue
): string {
  if ((error?.networkError as ServerError)?.statusCode === 500) {
    return intl.formatMessage({
      defaultMessage:
        "Application error - Scale To Win support staff have been notified.",
      description:
        "Error message shown when the server returns an internal error",
    });
  } else if (error.networkError?.message === "Failed to fetch") {
    return intl.formatMessage({
      defaultMessage:
        "Network error - please check your internet connection and try again.",
      description: "Error message shown when there is a network error",
    });
  }
  return error.message;
}

export function useErrorMessageFormatter(): (error: unknown) => string {
  const intl = useIntl();

  return function errorMessageFormatter(error: unknown): string {
    if (!error) {
      return "";
    } else if (isArray(error)) {
      return error.map(errorMessageFormatter).join(", ");
    } else if (error instanceof ApolloError) {
      return getApolloErrorMessage(error, intl);
    } else if (
      typeof error === "object" &&
      "message" in error &&
      error.message &&
      isString(error.message)
    ) {
      return error.message;
    } else {
      return error.toString();
    }
  };
}

export function graphqlErrorHasCode(
  code: string,
  errors: readonly (GraphQLError | GraphQLFormattedError)[]
): boolean {
  return errors.some(error => error.extensions?.code === code);
}
